import { useState, useEffect, useCallback, useRef } from "react";
import dayjs from "dayjs";
import apiService from "../services/apiService";

function UsersDashboard() {
  // State management
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialFetchDone = useRef(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    count: 0,
    total_pages: 0,
    page_size: 10,
    current_page: 1,
    has_next: false,
    has_previous: false,
    next_page: null,
    previous_page: null
  });

  // Controls state
  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState('date_joined'); // 'date_joined' or '-date_joined'

  // Fetch users data
  const fetchUsersData = useCallback(async (page = 1, size = pageSize, order = sortOrder) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: size,
        ordering: order
      };

      const data = await apiService.getUsersWithCounts(params);

      setUsersData(data.results || []);
      setPagination(data.pagination || {});
      setPageSize(size);
      setSortOrder(order);
    } catch (err) {
      setError("Failed to load users data.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pageSize, sortOrder]);

  // Initial load - only run once per component mount
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;
    
    fetchUsersData(1, 10, 'date_joined');
  }, []);

  // Event handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      fetchUsersData(page, pageSize, sortOrder);
    }
  };

  const handlePageSizeChange = (newSize) => {
    fetchUsersData(1, newSize, sortOrder);
  };

  const handleSortChange = (field) => {
    let newOrder = field;
    if (sortOrder === field) {
      newOrder = `-${field}`;
    } else if (sortOrder === `-${field}`) {
      newOrder = field;
    }
    fetchUsersData(1, pageSize, newOrder);
  };

  // Utility functions
  const formatDate = (dateString) => {
    return dayjs(dateString).format('DDMMM,YY hh.mmA').toUpperCase();
  };

  const getDisplayText = () => {
    const startIndex = (pagination.current_page - 1) * pagination.page_size + 1;
    const endIndex = Math.min(pagination.current_page * pagination.page_size, pagination.count);
    return `${startIndex} - ${endIndex} of ${pagination.count}`;
  };

  const getSortIcon = (field) => {
    if (sortOrder === field) return ' ↑';
    if (sortOrder === `-${field}`) return ' ↓';
    return '';
  };

  return (
    <div>
      {error && (
        <div style={{ padding: "1rem", backgroundColor: "#fee", color: "#c00", borderRadius: "4px", marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      <div className="course-detail-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: "1rem" }}>
        <div style={{ flex: 1 }}>
          <h1>Users Dashboard</h1>
          <p style={{ color: "var(--text-light)" }}>
            Overview of all users and their activity
          </p>
        </div>
        <div style={{
          padding: "1.5rem",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          minWidth: "200px",
          textAlign: "center",
          border: "2px solid var(--primary-color)"
        }}>
          <h3 style={{ margin: 0, fontSize: "0.9rem", color: "var(--text-light)", fontWeight: "500" }}>Total Users</h3>
          <p style={{
            fontSize: "2.5rem",
            fontWeight: "bold",
            margin: "0.5rem 0 0 0",
            color: "var(--primary-color)",
            lineHeight: "1"
          }}>
            {pagination.count}
          </p>
        </div>
      </div>

      {/* Controls Section */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "2rem",
        marginBottom: "1rem",
        flexWrap: "wrap",
        gap: "1rem"
      }}>
        {/* Page Size Selector */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <label style={{ fontWeight: "500", color: "var(--text-light)" }}>Show:</label>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            style={{
              padding: "0.5rem",
              border: "1px solid #ddd",
              borderRadius: "4px",
              backgroundColor: "white"
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ color: "var(--text-light)" }}>per page</span>
        </div>

        {/* Sort Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <span style={{ fontWeight: "500", color: "var(--text-light)" }}>Sort by:</span>
          <button
            onClick={() => handleSortChange('date_joined')}
            style={{
              padding: "0.5rem 1rem",
              border: "1px solid #ddd",
              borderRadius: "4px",
              backgroundColor: sortOrder.includes('date_joined') ? "var(--primary-color)" : "white",
              color: sortOrder.includes('date_joined') ? "white" : "var(--text-color)",
              cursor: "pointer"
            }}
          >
            Date Joined{getSortIcon('date_joined')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", marginTop: "1rem", position: "relative" }}>
        {loading && (
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            backgroundColor: "white",
            padding: "2rem",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
          }}>
            <div style={{ textAlign: "center", color: "var(--primary-color)", fontWeight: "500" }}>Loading...</div>
          </div>
        )}
        <table style={{
          width: "100%",
          borderCollapse: "collapse",
          backgroundColor: "white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          borderRadius: "8px",
          overflow: "hidden",
          opacity: loading ? 0.5 : 1,
          transition: "opacity 0.2s"
        }}>
          <thead>
            <tr style={{ backgroundColor: "var(--primary-color)", color: "white" }}>
              <th style={{ padding: "0.5rem", textAlign: "center", fontWeight: "600", width: "60px" }}>Sr.No.</th>
              <th style={{ padding: "0.5rem", textAlign: "left", fontWeight: "600" }}>Name</th>
              <th style={{ padding: "0.5rem", textAlign: "left", fontWeight: "600" }}>Email</th>
              <th style={{ padding: "0.5rem", textAlign: "left", fontWeight: "600" }}>Mobile</th>
              <th style={{ padding: "0.5rem", textAlign: "left", fontWeight: "600", maxWidth: "10%" }}>Institute</th>
              <th style={{ padding: "0.5rem", textAlign: "left", fontWeight: "600", maxWidth: "10%" }}>Department</th>
              <th style={{ padding: "0.5rem", textAlign: "left", fontWeight: "600", maxWidth: "10%" }}>Joined</th>
              <th style={{ padding: "0.5rem", textAlign: "center", fontWeight: "600" }}>Verified</th>
              <th style={{ padding: "0.5rem", textAlign: "right", fontWeight: "600" }}>Courses</th>
              <th style={{ padding: "0.5rem", textAlign: "right", fontWeight: "600" }}>QSets</th>
              <th style={{ padding: "0.5rem", textAlign: "right", fontWeight: "600" }}>Questions</th>
            </tr>
          </thead>
          <tbody>
            {usersData.length > 0 ? (
              usersData.map((user, index) => (
                <tr
                  key={user.id}
                  style={{
                    borderBottom: index < usersData.length - 1 ? "1px solid #e0e0e0" : "none",
                    transition: "background-color 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "white"}
                >
                  <td style={{ padding: "0.5rem", textAlign: "center", color: "var(--text-light)", fontWeight: "500" }}>
                    {(pagination.current_page - 1) * pagination.page_size + index + 1}
                  </td>
                  <td style={{ padding: "0.5rem" }}>
                    {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.username}
                  </td>
                  <td style={{ padding: "0.5rem", color: "var(--text-light)" }}>
                    {user.email}
                  </td>
                  <td style={{ padding: "0.5rem", color: "var(--text-light)" }}>
                    {user.mobile_number || '—'}
                  </td>
                  <td style={{ padding: "0.5rem", maxWidth: "10%", wordWrap: "break-word", whiteSpace: "normal" }}>
                    {user.institute?.name || 'Not assigned'}
                  </td>
                  <td style={{ padding: "0.5rem", maxWidth: "10%", wordWrap: "break-word", whiteSpace: "normal" }}>
                    {user.department || 'Not specified'}
                  </td>
                  <td style={{ padding: "0.5rem", maxWidth: "10%", wordWrap: "break-word", whiteSpace: "normal", color: "var(--text-light)" }}>
                    {formatDate(user.date_joined)}
                  </td>
                  <td style={{ padding: "0.5rem", textAlign: "center" }}>
                    {user.email_verified ? (
                      <span style={{
                        color: "#4caf50",
                        fontWeight: "bold"
                      }}>✓</span>
                    ) : (
                      <span style={{
                        color: "#f44336",
                        fontWeight: "bold"
                      }}>✗</span>
                    )}
                  </td>
                  <td style={{ padding: "0.5rem", textAlign: "right", fontWeight: "600" }}>
                    {user.number_of_courses}
                  </td>
                  <td style={{ padding: "0.5rem", textAlign: "right", fontWeight: "600" }}>
                    {user.number_of_quizzes}
                  </td>
                  <td style={{ padding: "0.5rem", textAlign: "right", fontWeight: "600" }}>
                    {user.number_of_questions}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" style={{ padding: "1rem", textAlign: "center", color: "var(--text-light)" }}>
                  No users data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {!loading && pagination.total_pages > 1 && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "2rem",
          flexWrap: "wrap",
          gap: "1rem"
        }}>
          {/* Left side - Item count */}
          <div style={{ color: "var(--text-light)" }}>
            Showing {getDisplayText()} users
          </div>

          {/* Right side - Pagination controls */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.current_page === 1}
              style={{
                padding: "0.5rem 1rem",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: pagination.current_page === 1 ? "#f5f5f5" : "white",
                color: pagination.current_page === 1 ? "#ccc" : "var(--text-color)",
                cursor: pagination.current_page === 1 ? "not-allowed" : "pointer"
              }}
            >
              First
            </button>

            <button
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={!pagination.has_previous}
              style={{
                padding: "0.5rem 1rem",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: !pagination.has_previous ? "#f5f5f5" : "white",
                color: !pagination.has_previous ? "#ccc" : "var(--text-color)",
                cursor: !pagination.has_previous ? "not-allowed" : "pointer"
              }}
            >
              Previous
            </button>

            <span style={{
              padding: "0.5rem 1rem",
              backgroundColor: "var(--primary-color)",
              color: "white",
              borderRadius: "4px",
              fontWeight: "500"
            }}>
              {pagination.current_page} / {pagination.total_pages}
            </span>

            <button
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={!pagination.has_next}
              style={{
                padding: "0.5rem 1rem",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: !pagination.has_next ? "#f5f5f5" : "white",
                color: !pagination.has_next ? "#ccc" : "var(--text-color)",
                cursor: !pagination.has_next ? "not-allowed" : "pointer"
              }}
            >
              Next
            </button>

            <button
              onClick={() => handlePageChange(pagination.total_pages)}
              disabled={pagination.current_page === pagination.total_pages}
              style={{
                padding: "0.5rem 1rem",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: pagination.current_page === pagination.total_pages ? "#f5f5f5" : "white",
                color: pagination.current_page === pagination.total_pages ? "#ccc" : "var(--text-color)",
                cursor: pagination.current_page === pagination.total_pages ? "not-allowed" : "pointer"
              }}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersDashboard;